import { Button, Modal } from "@mantine/core";
import type { AxiosError } from "axios";
import { useState } from "react";
import { type FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ModalFooter } from "../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../mantine/notifications/useToast";
import { getFieldSpecificError } from "../../utils/api-utils";
import { getErrorText } from "../../utils/get-error-text";
import { FormField } from "../BuildingBlocks/Forms/MantineFormFields/FormField";
import type { EnergyDataImportRequest } from "../EnergyData/EnergyDataUploadFlow/EnergyDataUpload";
import { requestSupportForDataUploadRequest } from "../EnergyData/EnergyDataUploadFlow/EnergyDataUpload";

interface SupportRequestProps {
  dataImportRequest: EnergyDataImportRequest;
  onClose: () => void;
  onSupportRequestSent: (
    updatedDataImportRequest: EnergyDataImportRequest
  ) => void;
}

function SupportModal({
  dataImportRequest,
  onClose,
  onSupportRequestSent
}: SupportRequestProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const [commentValue, setCommentValue] = useState("");
  const [inputErrorText, setInputErrorText] = useState<
    FieldError | undefined
  >();

  function handleRequestSupport(): void {
    if (!isValidInput(commentValue)) {
      showInputIfInvalidCommentValue();
      return;
    }

    requestSupportForDataUploadRequest(dataImportRequest.id, commentValue)
      .then((response) => {
        onSupportRequestSent(response.data);
      })
      .catch((e: AxiosError) => {
        const errorMessage = getFieldSpecificError(e);
        const errorText = getErrorText(
          e,
          errorMessage && e.response?.status
            ? { [e.response.status]: errorMessage }
            : {}
        );

        showErrorToast(errorText);
      });
  }

  function handleChangeCommentInput(value: string) {
    setInputErrorText(undefined);
    setCommentValue(value);
  }

  function isValidInput(value: string) {
    return value.trim().length <= 512;
  }

  function showInputIfInvalidCommentValue() {
    setInputErrorText({
      message: t("errors.TooLongText", { maxLength: 512 }),
      type: "maxLength"
    });
  }

  return (
    <Modal
      className="SupportModal"
      opened
      title="Supportanfrage"
      onClose={onClose}
    >
      <div className="intro">
        <p>
          Bitte schreiben Sie uns eventuelle Besonderheiten der Datei oder des
          aufgetretenen Fehlers und bestätigen die Anfrage.
        </p>
      </div>
      <form>
        <FormField
          autosize
          error={inputErrorText}
          label="Kommentar"
          minRows={9}
          name="comment"
          resize="vertical"
          type="textarea"
          value={commentValue}
          onChange={(e) => handleChangeCommentInput(e.target.value)}
        />
      </form>
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>{" "}
        <Button color="secondary" onClick={handleRequestSupport}>
          Einsenden
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { SupportModal };
